// LoadingSplash — a thin mint arc + letterspaced label that owns the
// screen between first paint and the moment R3F has finished booting.
// Once useWebGLAvailable() reports true AND the R3F <Canvas> has fired
// its onCreated callback, the splash fades out (opacity 1 -> 0, 400ms,
// ease-out-expo) and unmounts itself.
//
// If WebGL is not available the splash stays forever and shows a
// "your browser doesn't support WebGL" message with a link to the
// static text-only fallback page.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWebGLAvailable } from '../three/useWebGLAvailable';

const FADE_MS = 400;
const SPIN_MS = 1100;

export interface LoadingSplashProps {
  /**
   * Called once the R3F Canvas has finished its onCreated callback. The
   * splash waits for BOTH this prop turning true AND WebGL being
   * available before fading out.
   */
  canvasReady?: boolean;
}

/**
 * Top-level loading splash. Mount it once at the very top of the React
 * tree so it covers the scene underneath during the initial mount.
 */
export function LoadingSplash({ canvasReady = false }: LoadingSplashProps) {
  const hasWebGL = useWebGLAvailable();
  const [mounted, setMounted] = useState(true);

  // Fade out only when BOTH conditions hold:
  //   - useWebGLAvailable() returned true (WebGL works)
  //   - the R3F <Canvas onCreated> has fired (canvasReady)
  // When WebGL is unavailable we leave the splash up forever and switch
  // to the "no WebGL" message + fallback link.
  useEffect(() => {
    if (!hasWebGL) return;
    if (!canvasReady) return;
    const id = window.setTimeout(() => setMounted(false), FADE_MS);
    return () => window.clearTimeout(id);
  }, [hasWebGL, canvasReady]);

  if (!mounted) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="loading-splash"
        className="loading-splash"
        initial={{ opacity: 1 }}
        animate={{ opacity: hasWebGL && canvasReady ? 0 : 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: FADE_MS / 1000, ease: [0.22, 1, 0.36, 1] }}
        role={hasWebGL ? 'status' : 'alert'}
        aria-live={hasWebGL ? 'polite' : 'assertive'}
        aria-label={
          hasWebGL
            ? 'Loading the planet'
            : 'WebGL is not available in this browser'
        }
      >
        {hasWebGL ? <SpinnerState /> : <NoWebGLState />}
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * The default visual: a thin mint arc rotating above an uppercase
 * letterspaced mono label.
 */
function SpinnerState() {
  return (
    <div className="loading-splash__inner">
      <svg
        className="loading-splash__spinner"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        aria-hidden="true"
      >
        <circle
          cx="16"
          cy="16"
          r="13"
          fill="none"
          stroke="rgba(245,240,232,0.12)"
          strokeWidth="2"
        />
        <g style={{ transformOrigin: '16px 16px' }}>
          <circle
            cx="16"
            cy="16"
            r="13"
            fill="none"
            stroke="#7fb89f"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="50 100"
            className="loading-splash__arc"
          />
        </g>
      </svg>
      <p className="loading-splash__label">loading the planet</p>
    </div>
  );
}

/**
 * Stays on screen forever when WebGL is unavailable. The label fades
 * out and a static error block fades in, with a link to the text-only
 * fallback page.
 */
function NoWebGLState() {
  return (
    <div className="loading-splash__inner loading-splash__inner--nowebgl">
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        aria-hidden="true"
        style={{ opacity: 0.35 }}
      >
        <circle
          cx="16"
          cy="16"
          r="13"
          fill="none"
          stroke="#f5f0e8"
          strokeWidth="2"
          strokeDasharray="3 4"
        />
      </svg>
      <p className="loading-splash__label">
        your browser doesn&rsquo;t support WebGL
      </p>
      <p className="loading-splash__sub">
        A text-only version is available at{' '}
        <a href="/fallback.html" className="loading-splash__fallback-link">
          /fallback.html
        </a>
        .
      </p>
    </div>
  );
}

// Spinner animation lives in index.css so it survives the unmount of
// this component without needing a JS-driven rAF loop. 1100ms matches
// the project's --d-slow token.
export const LOADING_SPIN_DURATION_MS = SPIN_MS;

export default LoadingSplash;